import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaUserSecret, FaUserCheck, FaSearch, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

// API 기본 URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8002';

const AdminModePanel = () => {
  const { getCurrentUser, isAdmin, isAdminMode, isGroupOwner, originalAdminUser, impersonateUser, api, stopImpersonation } = useAuth();
  const currentUser = getCurrentUser();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // 패널 콘텐츠 참조 생성
  const adminPanelRef = useRef(null);

  // 'admin-panel-open' 이벤트 감지하는 useEffect 추가
  useEffect(() => {
    const handleExpandPanel = () => {
      setIsExpanded(true);
      fetchUsers();
    };
    
    window.addEventListener('admin-panel-open', handleExpandPanel);
    
    return () => {
      window.removeEventListener('admin-panel-open', handleExpandPanel);
    };
  }, []);

  // 패널 바깥 클릭 감지 useEffect 추가
  useEffect(() => {
    // mousedown 이벤트 핸들러 함수
    const handleClickOutside = (event) => {
      // 패널 참조(ref)가 있고, 클릭된 요소가 패널 콘텐츠 내부에 포함되지 않은 경우
      if (adminPanelRef.current && !adminPanelRef.current.contains(event.target)) {
        // 예외: 관리자 모드 버튼 자체를 클릭한 경우는 무시 (버튼이 토글 역할)
        const adminButton = document.querySelector('[data-el="admin-button"]'); // 버튼에 data-el 속성 추가 필요
        if (adminButton && adminButton.contains(event.target)) {
          return;
        }
        setIsExpanded(false); // 패널 닫기
      }
    };

    // 패널이 보여질 때만 이벤트 리스너 추가
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // cleanup 함수: 컴포넌트 언마운트 또는 패널 닫힐 때 리스너 제거
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]); // isExpanded 상태가 변경될 때마다 effect 재실행

  // 관리자가 아닌 경우 렌더링 안함
  if (!isAdmin()) {
    return null;
  }

  // 사용자 검색 함수
  const searchUsers = async () => {
    if (!searchText.trim()) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // 검색 API 엔드포인트 사용
      const response = await api.get(`/api/v1/auth/admin/users?query=${encodeURIComponent(searchText)}`);
      
      if (response.status === 200) {
        // 응답 데이터 구조 확인 및 처리
        const data = response.data;
        
        // users 필드가 배열로 제공됨
        if (data.users && Array.isArray(data.users)) {
          // nickname 필드가 없는 경우 name 필드로 대체
          const processedUsers = data.users.map(user => {
            if (!user.nickname && user.name) {
              user.nickname = user.name;
            } else if (!user.nickname) {
              user.nickname = '사용자 ' + user.id;
            }
            return user;
          });
          
          setUsers(processedUsers);
        } else if (Array.isArray(data)) {
          // 결과가 바로 배열인 경우
          const processedUsers = data.map(user => {
            if (!user.nickname && user.name) {
              user.nickname = user.name;
            } else if (!user.nickname) {
              user.nickname = '사용자 ' + user.id;
            }
            return user;
          });
          
          setUsers(processedUsers);
        } else {
          console.error('API 응답에 users 배열이 없거나 배열 형식이 아닙니다:', data);
          setUsers([]);
        }
      } else {
        setError('사용자 검색에 실패했습니다.');
      }
    } catch (error) {
      setError('사용자 검색에 실패했습니다.');
      console.error('사용자 검색 오류:', error);
      
      // 개발 환경에서 테스트를 위한 더미 데이터
      setUsers([
        { id: 1, nickname: '사용자1', email: 'user1@example.com' },
        { id: 2, nickname: '사용자2', email: 'user2@example.com' },
        { id: 3, nickname: '사용자3', email: 'user3@example.com' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // 사용자 목록 불러오기
  const fetchUsers = async () => {
    if (isLoading) return; // 이미 로딩 중이면 중복 호출 방지
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get('/api/v1/admin/users');
      
      // 응답 데이터 확인 및 가공
      if (Array.isArray(response.data.users)) {
        // 각 사용자의 nickname 필드 처리
        const processedUsers = response.data.users.map(user => {
          // nickname 필드가 없는 경우 name 필드로 대체
          if (!user.nickname && user.name) {
            user.nickname = user.name;
          } else if (!user.nickname) {
            user.nickname = '사용자 ' + user.id;
          }
          return user;
        });
        
        setUsers(processedUsers);
      } else {
        // 데이터 형식이 예상과 다른 경우
        setUsers([]);
        setError('사용자 데이터 형식이 올바르지 않습니다.');
      }
    } catch (error) {
      console.error('[AdminPanel] 사용자 목록 가져오기 실패:', error);
      setError('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 사용자 가장 처리
  const handleUserClick = async (userId) => {
    try {
      setIsProcessing(true);
      const success = await impersonateUser(userId);
      
      if (success) {
        setIsProcessing(false);
        setIsExpanded(false);
      } else {
        setIsProcessing(false);
        // 에러 처리
      }
    } catch (error) {
      console.error('[AdminPanel] 사용자 가장 실패:', error);
      setIsProcessing(false);
    }
  };

  // 가장 모드 해제 처리
  const handleStopImpersonation = async () => {
    try {
      setIsProcessing(true);
      await stopImpersonation();
      // stopImpersonation 함수 내에서 페이지 리로드를 처리하므로 
      // 여기서는 추가 작업이 필요하지 않음
    } catch (error) {
      console.error('[AdminPanel] 가장 모드 해제 실패:', error);
      setIsProcessing(false);
    }
  };

  return (
    <AdminPanelContainer isAdminMode={isAdminMode} isGroupOwner={isGroupOwner}>
      {/* 접힌 상태에서 보이는 버튼 */}
      <AdminButton onClick={() => setIsExpanded(!isExpanded)} data-el="admin-button">
        {isAdminMode ? <FaUserSecret /> : <FaUserCheck />}
      </AdminButton>

      {/* 열린 상태의 패널 */}
      {isExpanded && (
        <AdminPanelContent ref={adminPanelRef}>
          <PanelHeader>
            <PanelTitle>관리자 모드</PanelTitle>
            <CloseButton onClick={() => setIsExpanded(false)}>
              <FaTimes />
            </CloseButton>
          </PanelHeader>

          {/* 현재 상태 표시 */}
          <StatusSection>
            {isAdminMode ? (
              <>
                <StatusText>
                  <strong>{originalAdminUser?.nickname || originalAdminUser?.name || originalAdminUser?.email || '관리자'}</strong>님이 
                  <strong>{currentUser?.nickname || currentUser?.name || currentUser?.email || '사용자'}</strong>님으로 가장 중
                </StatusText>
                <BackButtonHighlight onClick={handleStopImpersonation}>
                  ⚠️ 가장 모드 해제하기 ⚠️
                </BackButtonHighlight>
                <RefreshInfoText>
                  가장 모드 해제 후 자동으로 새로고침됩니다.
                </RefreshInfoText>
              </>
            ) : (
              <StatusText>
                <strong>{currentUser?.nickname || currentUser?.name || currentUser?.email || '관리자'}</strong>님은 관리자입니다
              </StatusText>
            )}
          </StatusSection>

          {/* 사용자 검색 영역 */}
          <SearchSection>
            <SearchTitle>다른 사용자로 가장하기</SearchTitle>
            
            {isAdminMode ? (
              <DisabledSearchMessage>
                가장 모드 중에는 다른 사용자 검색이 비활성화됩니다.
                가장 모드를 해제한 후 다시 시도해주세요.
              </DisabledSearchMessage>
            ) : (
              <>
                <SearchInputWrapper>
                  <SearchInput
                    type="text"
                    placeholder="사용자 이름 또는 이메일 검색"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                  />
                  <SearchButton onClick={searchUsers}>
                    <FaSearch />
                  </SearchButton>
                </SearchInputWrapper>
                
                {error && <ErrorMessage>{error}</ErrorMessage>}
                
                {isLoading ? (
                  <LoadingText>검색 중...</LoadingText>
                ) : (
                  <UserList>
                    {users.map(user => (
                      <UserItem key={user.id} onClick={() => handleUserClick(user.id)}>
                        <UserName>{user.nickname || user.name || '사용자 ' + user.id}</UserName>
                        <UserEmail>{user.email || '이메일 없음'}</UserEmail>
                      </UserItem>
                    ))}
                  </UserList>
                )}
              </>
            )}
          </SearchSection>
        </AdminPanelContent>
      )}
    </AdminPanelContainer>
  );
};

// 스타일 컴포넌트
const AdminPanelContainer = styled.div`
  position: fixed;
  left: 20px;
  z-index: 997;
  bottom: ${({ isAdminMode, isGroupOwner }) =>
    isAdminMode && isGroupOwner ? '80px' : '20px'};
`;

const AdminButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #673ab7; /* 보라색 - 관리자 색상 */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  font-size: 20px;
  
  &:hover {
    background-color: #5e35b1;
  }
`;

const AdminPanelContent = styled.div`
  position: absolute;
  bottom: 60px;
  left: 0;
  width: 300px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #673ab7;
  color: white;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const StatusSection = styled.div`
  padding: 16px;
  border-bottom: 1px solid #eee;
`;

const StatusText = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #333;
  
  strong {
    font-weight: 600;
    color: #673ab7;
  }
`;

const RefreshInfoText = styled.p`
  margin: 8px 0;
  font-size: 12px;
  color: #666;
  text-align: center;
  font-style: italic;
`;

const SearchSection = styled.div`
  padding: 16px;
 `;

const SearchTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px 0 0 4px;
  padding: 8px 12px;
  font-size: 14px;
  width: 50%;
  
  &:focus {
    outline: none;
    border-color: #673ab7;
  }
`;

const SearchButton = styled.button`
  background-color: #673ab7;
  color: white;
  border: none;
  border-radius: 0 4px 4px 0;
  padding: 0 12px;
  cursor: pointer;
  
  &:hover {
    background-color: #5e35b1;
  }
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 13px;
  margin: 8px 0;
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 14px;
  margin: 16px 0;
  text-align: center;
`;

const UserList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
`;

const UserItem = styled.li`
  padding: 10px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  
  &:hover {
    background-color: #f9f5ff;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const UserName = styled.div`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const UserEmail = styled.div`
  color: #666;
  font-size: 12px;
`;

const BackButtonHighlight = styled.button`
  background-color: #ffe0e0;
  color: #d32f2f;
  border: 1px solid #ffb0b0;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #ffb0b0;
  }
`;

const DisabledSearchMessage = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 12px;
  margin: 10px 0;
  font-size: 14px;
  color: #6c757d;
  text-align: center;
  line-height: 1.5;
`;

export default AdminModePanel; 